import axios from 'axios';
import { v4 } from 'uuid';
import _ from 'lodash';
import { getI18n } from 'react-i18next';
import Constants from '../utils/Constants';
import { createLogger } from '../utils/logging';
import { showErrorNotification, showInfoNotification } from './notifications';
import { createErrorMessage } from '../components/errors/Errors';
import { ERROR_FILE_UPLOAD_EXTERNAL_UNKNOWN_ERROR } from '../components/errors/UploadErrors';
import { isExtraBackendConfirmationNeeded } from '../components/errors/MiscErrors';
import {
    addCorrelation,
    removeCorrelation,
} from './correlations';

const logger = createLogger('Actions-FileUploads');

export const onUploadProgress = (fileName, dispatch) => (progressEvent) => {
    const progress = Math.round((100 * progressEvent.loaded) / progressEvent.total);
    dispatch(updateFileUpload(fileName, progress));
};

export const uploadFile = (file, cancelTokenSource, uploadPath, accessToken, variables, onUpload) => (dispatch) => {
    const formData = new FormData();
    formData.append('file', file);
    const newCorrelationId = v4();
    dispatch(addCorrelation(newCorrelationId));
    formData.append('correlationId', newCorrelationId);
    variables && variables.forEach((variable) => {
        if (variable.name === 'file') {
            throw new Error('Can not use "file" as a variable name for file upload!');
        } else if (variable.name === 'correlationId') {
            throw new Error('Can not use "correlationId" as a variable name for file upload!');
        }
        formData.append(variable.name, variable.value);
    });
    axios.post(Constants.UPLOAD_PATH + uploadPath, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `${Constants.BEARER_PREFIX} ${accessToken}`,
        },
        onUploadProgress: onUploadProgress(file.name, dispatch),
        cancelToken: cancelTokenSource.token,
    })
        .then(onUploadResponse(dispatch, file.name, onUpload))
        .catch(onUploadError(dispatch, file.name, newCorrelationId));
};

export const onUploadResponse = (dispatch, fileName, onUpload) => (response) => {
    dispatch(completeFileUpload(fileName, response));
    dispatch(showInfoNotification(getI18n().t('file-upload.notification.file-upload-success', { fileName })));
    onUpload && onUpload();
};

export const onUploadError = (dispatch, fileName, correlationId) => (error) => {
    const i18n = getI18n();
    if (axios.isCancel(error)) {
        logger.warn(error.message);
        dispatch(cancelFileUpload(fileName));
    } else {
        const { response } = error;
        if (response && response.data && _.isArray(response.data)) {
            if (isExtraBackendConfirmationNeeded(response.data)) {
                dispatch(extraUploadConfirmationNeeded());
                dispatch(removeCorrelation(correlationId));
            } else {
                response.data.forEach((errObject) => {
                    const message = createErrorMessage(errObject.code, errObject.params);
                    dispatch(showErrorNotification(i18n.t('file-upload.error.long-prefix', { fileName, message })));
                    dispatch(errorFileUpload(fileName, errObject.code, errObject.params));
                });
            }
        } else {
            const errorMessageCode = ERROR_FILE_UPLOAD_EXTERNAL_UNKNOWN_ERROR;
            const errorMessageParams = { message: error.message };
            const message = createErrorMessage(errorMessageCode, errorMessageParams);
            dispatch(showErrorNotification(i18n.t('file-upload.error.long-prefix', { fileName, message })));
            dispatch(errorFileUpload(fileName, errorMessageCode, errorMessageParams));
        }
    }
};

export const EXTRA_UPLOAD_CONFIRMATION_NEEDED = 'EXTRA_UPLOAD_CONFIRMATION_NEEDED';
export const extraUploadConfirmationNeeded = () => ({ type: EXTRA_UPLOAD_CONFIRMATION_NEEDED });

export const EXTRA_UPLOAD_CONFIRMATION_NOT_NEEDED = 'EXTRA_UPLOAD_CONFIRMATION_NOT_NEEDED';
export const extraUploadConfirmationNotNeeded = () => ({ type: EXTRA_UPLOAD_CONFIRMATION_NOT_NEEDED });

export const INIT_FILE_UPLOADS = 'INIT_FILE_UPLOADS';
export const initFileUploads = (fileDescriptors) => ({
    type: INIT_FILE_UPLOADS,
    payload: fileDescriptors.map((fileDescriptor) => fileDescriptor.file.name),
});

export const UPDATE_FILE_UPLOAD = 'UPDATE_FILE_UPLOAD';
export const updateFileUpload = (fileName, progress) => ({
    type: UPDATE_FILE_UPLOAD,
    payload: {
        fileName,
        progress,
    },
});

export const REMOVE_FILE_UPLOAD = 'REMOVE_FILE_UPLOAD';
export const removeFileUpload = (fileName) => ({
    type: REMOVE_FILE_UPLOAD,
    payload: fileName,
});

export const REMOVE_ALL_FILE_UPLOADS = 'REMOVE_ALL_FILE_UPLOADS';
export const removeAllFileUploads = () => ({
    type: REMOVE_ALL_FILE_UPLOADS,
});

export const CANCEL_FILE_UPLOAD = 'CANCEL_FILE_UPLOAD';
export const cancelFileUpload = (fileName) => ({
    type: CANCEL_FILE_UPLOAD,
    payload: fileName,
});

export const COMPLETE_FILE_UPLOAD = 'COMPLETE_FILE_UPLOAD';
export const completeFileUpload = (fileName, response) => ({
    type: COMPLETE_FILE_UPLOAD,
    payload: {
        fileName,
        responseData: response.data,
        responseFileName: response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, ''),
    },
});

export const ERROR_FILE_UPLOAD = 'ERROR_FILE_UPLOAD';
export const errorFileUpload = (fileName, errorMessageCode, errorMessageParams) => ({
    type: ERROR_FILE_UPLOAD,
    payload: {
        fileName,
        errorMessageCode,
        errorMessageParams,
    },
});
